import type { SlotSpellDef, SlotSpellId } from '../core/types';
import type { NodeLevels } from '../core/skilltree';
import { BALANCE } from './balance';

/**
 * Sorts d'**emplacement** — COMMENTS v0.4.1 §1.4.
 *
 * Ils se débloquent tout seuls avec le nombre d'emplacements de dés Maîtres
 * (`DerivedStats.slots`) et restent acquis quand le joueur en débloque
 * d'autres. Ils s'évaluent sur les faces des dés **Maîtres** uniquement (la
 * face d'un dé absorbé par un bouclier compte quand même).
 *
 * Un seul sort d'emplacement se déclenche par lancer (A6 / **A16**) :
 * *Yeux du serpent* prime quand toutes les faces sont minimales, sinon c'est le
 * **multiplicateur de dégâts le plus fort**, *Suite* et *Full* compris (dont le
 * multiplicateur dépend des faces obtenues).
 *
 * v0.7.0 (COMMENTS v0.6.0 §1.2.1) : *Double paire* et *Carré* arrivent avec le
 * 4e emplacement, *Suite* et *Full* avec le 5e ; *666* quitte la famille pour
 * devenir un sort de **dé** (§1.2.2).
 */
export const SLOT_SPELLS: SlotSpellDef[] = [
  {
    id: 'pair',
    name: 'Paire',
    requiredSlots: 2,
    multiplier: BALANCE.spellPairMultiplier,
    condition: 'Deux dés Maîtres affichent la même face',
    description: 'Lors d’une paire, multiplie les dégâts du lancer par 1,25.',
  },
  {
    id: 'snakeEyes',
    name: 'Yeux du serpent',
    requiredSlots: 2,
    multiplier: BALANCE.spellSnakeEyesMultiplier,
    condition: 'Tous les dés Maîtres affichent leur face minimale (1)',
    description:
      'Si tous vos dés affichent une face minimale, multiplie les dégâts du lancer par 0,75.',
  },
  {
    id: 'triple',
    name: 'Brelan',
    requiredSlots: 3,
    multiplier: BALANCE.spellTripleMultiplier,
    condition: 'Trois dés Maîtres affichent la même face',
    description: 'Lors d’un brelan, multiplie les dégâts par 2.',
  },
  {
    id: 'doublePair',
    name: 'Double paire',
    requiredSlots: 4,
    multiplier: BALANCE.spellDoublePairMultiplier,
    condition: 'Deux couples de dés Maîtres affichent la même face (faces différentes)',
    description: 'Lors d’une double paire, multiplie les dégâts du lancer par 2,5.',
  },
  {
    id: 'four',
    name: 'Carré',
    requiredSlots: 4,
    multiplier: BALANCE.spellFourMultiplier,
    condition: 'Quatre dés Maîtres affichent la même face',
    description: 'Lors d’un carré, multiplie les dégâts du lancer par 4.',
  },
  {
    id: 'straight',
    name: 'Suite',
    requiredSlots: 5,
    multiplier: 5,
    dynamic: true,
    condition: 'Cinq dés Maîtres affichent des faces consécutives',
    description:
      'Lors d’une suite, multiplie les dégâts du lancer par la plus haute face de la suite.',
  },
  {
    id: 'full',
    name: 'Full',
    requiredSlots: 5,
    multiplier: 6,
    dynamic: true,
    condition: 'Un brelan et une paire de faces différentes',
    description:
      'Lors d’un full, multiplie les dégâts du lancer par le produit des faces du brelan ' +
      'et de la paire.',
  },
  // ── v0.47.0 — *Qui a dit Poker ?* (`aby_poker`, A94-10). Six ou sept dés
  // Maîtres : ils demandent les emplacements **et** le nœud.
  {
    id: 'triplePair',
    name: 'Triple paire',
    requiredSlots: 6,
    multiplier: BALANCE.spellTriplePairMultiplier,
    requiresNode: 'aby_poker',
    condition: 'Trois couples de dés Maîtres affichent la même face (faces différentes)',
    description: 'Lors d’une triple paire, multiplie les dégâts du lancer par 3,5.',
  },
  {
    id: 'doubleTriple',
    name: 'Double brelan',
    requiredSlots: 6,
    multiplier: BALANCE.spellDoubleTripleMultiplier,
    requiresNode: 'aby_poker',
    condition: 'Deux brelans de dés Maîtres de faces différentes',
    description: 'Lors d’un double brelan, multiplie les dégâts du lancer par 5.',
  },
  {
    id: 'grandStraight',
    name: 'Grande suite',
    requiredSlots: 6,
    multiplier: 36,
    dynamic: true,
    requiresNode: 'aby_poker',
    condition: 'Six dés Maîtres affichent des faces consécutives',
    description:
      'Lors d’une grande suite, multiplie les dégâts du lancer par la plus haute face de la ' +
      'suite au carré.',
  },
  {
    id: 'maxiStraight',
    name: 'Maxi suite',
    requiredSlots: 7,
    multiplier: 343,
    dynamic: true,
    requiresNode: 'aby_poker',
    condition: 'Sept dés Maîtres affichent des faces consécutives',
    description:
      'Lors d’une maxi suite, multiplie les dégâts du lancer par la plus haute face de la ' +
      'suite au cube.',
  },
  {
    id: 'tripleFour',
    name: 'Brelan carré',
    requiredSlots: 7,
    multiplier: 30,
    dynamic: true,
    requiresNode: 'aby_poker',
    condition: 'Un brelan et un carré de faces différentes',
    description:
      'Lors d’un brelan carré, multiplie les dégâts du lancer par la face du brelan et par ' +
      'celle du carré.',
  },
];

export const SLOT_SPELL_BY_ID: Record<SlotSpellId, SlotSpellDef> = Object.fromEntries(
  SLOT_SPELLS.map((s) => [s.id, s]),
) as Record<SlotSpellId, SlotSpellDef>;

/**
 * Sorts d'emplacement débloqués par `slots` emplacements (1 → aucun). Un sort
 * qui demande un nœud (`requiresNode`, *Poker* — A94-10) n'est rendu que si
 * `levels` le porte au niveau 1 au moins : sans `levels`, jamais.
 */
export function slotSpellsFor(slots: number, levels?: NodeLevels): SlotSpellId[] {
  return SLOT_SPELLS.filter(
    (s) =>
      slots >= s.requiredSlots &&
      (!s.requiresNode || ((levels?.[s.requiresNode] ?? 0) >= 1)),
  ).map((s) => s.id);
}
